import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { pdf } from '@react-pdf/renderer';
import { FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useServiceModuleConfig } from '@/hooks/useServiceModuleConfig';
import { toBase64OrUrl } from '@/utils/pdfGenerator';
import { generatePdfFileName } from '@/utils/fileNameUtils';
import { imageToJpegDataUrl } from '@/utils/imageToJpegDataUrl';
import { supabase } from '@/integrations/supabase/client';
import ServiceOrderReceiptDocument from './ServiceOrderReceiptDocument';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  order: any;
}

export default function ServiceOrderReceiptButton({ order }: Props) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { company } = useAuth();
  const { data: moduleConfig } = useServiceModuleConfig();

  async function handlePrint() {
    setLoading(true);
    try {
      // Logo em base64 — mesmo helper usado pelos PDFs de orçamento
      const logoBase64 = company?.logo_url
        ? await toBase64OrUrl(company.logo_url)
        : undefined;

      // Fotos da OS: espelha o que useServiceOrderPhotos faz (query direta +
      // signed URL no bucket privado), converte WebP→JPEG antes do render.
      // Falha na conversão de UMA foto não aborta o PDF: pula a foto.
      const photos: Array<{ dataUrl: string; caption?: string | null }> = [];
      const { data: photoRows } = await supabase
        .from('service_order_photos')
        .select('id, storage_path, caption, position')
        .eq('service_order_id', order.id)
        .order('position', { ascending: true });

      for (const photo of photoRows ?? []) {
        try {
          const { data: signed } = await supabase.storage
            .from('service-order-images')
            .createSignedUrl(photo.storage_path, 3600);
          if (!signed?.signedUrl) continue;
          const dataUrl = await imageToJpegDataUrl(signed.signedUrl);
          photos.push({ dataUrl, caption: photo.caption });
        } catch (photoErr) {
          Sentry.captureException(photoErr, {
            extra: { service_order_id: order.id, photo_id: photo.id },
          });
        }
      }

      const blob = await pdf(
        <ServiceOrderReceiptDocument
          order={order}
          moduleConfig={moduleConfig ?? null}
          photos={photos}
          company={{
            name:              company?.name ?? 'Empresa',
            razao_social:      company?.razao_social,
            cnpj:              company?.cnpj,
            endereco:          company?.endereco,
            numero:            company?.numero,
            bairro:            company?.bairro,
            cidade:            company?.cidade,
            estado:            company?.estado,
            cep:               company?.cep,
            telefone:          company?.telefone,
            email_corporativo: company?.email_corporativo,
          }}
          logoBase64={logoBase64}
        />
      ).toBlob();

      // Download via <a> click — espelha o dialog de orçamento (não abre aba)
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generatePdfFileName(
        order.customers?.name ?? 'Cliente',
        'Coleta',
        order.service_order_number ?? 'SN',
        { includeDate: true, dateFormat: 'dd-MM-yyyy' },
        { date: new Date() },
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      Sentry.captureException(err, {
        extra: { service_order_id: order.id },
      });
      const message =
        err instanceof Error ? err.message : 'Erro ao gerar PDF';
      toast({
        title: 'Erro ao gerar comprovante',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Button variant="outline" size="sm" onClick={handlePrint} disabled={loading}>
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Gerando…
        </>
      ) : (
        <>
          <FileText className="w-4 h-4 mr-2" />
          Comprovante de Coleta
        </>
      )}
    </Button>
  );
}
